import { useCallback, useState } from 'react';

import toast from 'components/partials/toast/toast';

import { copyToClipboard } from 'helpers/copy-to-clipboard';

import { COPY_CONFIRMATION_LABEL } from '../ai-assistant.constants';

import {
  useTimedConfirmation,
  type ConfirmationPhase,
} from './use-timed-confirmation';

export interface UseMessageCopyResult {
  /** True while the badge is shown or fading out. */
  isShowingCopyConfirmation: boolean;
  copyConfirmationPhase: ConfirmationPhase;
  /** Live region text; alternates a zero-width space so VoiceOver re-announces. */
  copyAnnouncement: string;
  copyContent: () => Promise<boolean>;
}

/** Clipboard copy with visual confirmation and screen-reader announcement. */
export const useMessageCopy = (content: string): UseMessageCopyResult => {
  const { phase, showConfirmation } = useTimedConfirmation();
  const [announcement, setAnnouncement] = useState('');

  const copyContent = useCallback(async (): Promise<boolean> => {
    try {
      await copyToClipboard(content);
      showConfirmation();

      setAnnouncement((prev) =>
        prev === COPY_CONFIRMATION_LABEL
          ? `${COPY_CONFIRMATION_LABEL}\u200B`
          : COPY_CONFIRMATION_LABEL
      );

      return true;
    } catch {
      toast.error({ title: 'Failed to copy' });

      return false;
    }
  }, [content, showConfirmation]);

  return {
    isShowingCopyConfirmation: phase !== 'idle',
    copyConfirmationPhase: phase,
    copyAnnouncement: announcement,
    copyContent,
  };
};
